import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
    Card,
    Button,
    makeStyles,
    Subtitle2,
    Spinner,
} from '@fluentui/react-components';
import {
    ChevronDown20Regular,
    ChevronUp20Regular,
} from '@fluentui/react-icons';
import { RootState, addPseudoMessage } from '../../utils/store';
import { ChatBundle, CopilotService } from '../../Service/ServerAPI';
import { useTokenService } from '../../tokenService';
import Segment from '../Segment/Segment';
import Script from '../Script/Script';
import FeedbackBar from '../FeedbackBar/FeedbackBar';
import ScriptRetryPrompt from '../ScriptRetryPrompt/ScriptRetryPrompt';
import Collapse from '../Collapse';
import TextMessage from './TextMessage';
import LuaScriptMessage from '../CampaignGPT/ComboMessage';
import ContextPrompt from '../ContextPrompt/ContextPrompt';
import AccessInstructions from './AccessInstructions';
import ZeroCountBanner from './ZeroCountBanner';
import CancellationTextMessage from './CancellationTextMessage';
import ReasoningAccordion from './ReasoningAccordion';

const useStyles = makeStyles({
    messageContainer: {
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#292C31',
        borderRadius: '20px',
        color: '#fff',
        padding: '20px',
        marginBottom: '16px',
        opacity: 0.85,
        border: '0.5px solid rgba(255, 255, 255, 0.1)',
    },
    toggleButton: {
        width: 'fit-content',
        borderRadius: '8px',
    },
    feedbackContainer: {
        width: '400px',
        paddingTop: '16px',
        marginBottom: '16px',
        background: 'transparent',
        boxShadow: 'none',
        position: 'relative',
        zIndex: 1,
    },
    segmentScriptContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px 0',
        transition: '0.3s all ease-in-out',
        fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
    },
});

interface MessageProps {
    
    message: ChatBundle;
    onLuaScriptChange: (code: string) => void;
    onSend: () => void;
    lastMessage: boolean;
    hardcoded: boolean;
    messageIndex: number;
    messageSequenceIndex: number;
    isProcessing?: boolean;
    processNotificationIndex?: number;
}

const Message: React.FC<MessageProps> = ({
    message,
    onLuaScriptChange,
    onSend,
    lastMessage,
    hardcoded,
    messageIndex,
    messageSequenceIndex,
    isProcessing = false,
    processNotificationIndex
}) => {
    const [viewScript, setViewScript] = useState(false);
    const [offerRetry, setOfferRetry] = useState(true);
    const [promptContext, setPromptContext] = useState(true);
    const [isScriptRetrying, setScriptRetrying] = useState(false);
    const [showZeroCountBanner, setZeroCountBanner] = useState(false);
    const [showCancellationNotification, setShowCancellationNotification] = useState(false);
    const dispatch = useDispatch();
    const settings = useSelector((state: RootState) => state.settings);
    const getAccessToken = useTokenService();
    const classes = useStyles();
    const copilotService = CopilotService.getInstance(getAccessToken);

    const [isPopupVisible, setPopupVisible] = useState(false);

    const toggleIrisPopup = () => {
      setPopupVisible(!isPopupVisible);
    };

    const retryScripGeneration = () => {
        if (
            message.script &&
            message.script?.script_error &&
            message.response?.script
        ) {
            copilotService
                .RetryScriptAPI(
                    message.response?.script ?? '',
                    message.script?.script_error ?? '',
                    settings.apiEndpoint,
                    settings.sessionId
                )
                .then((response) => {
                    setScriptRetrying(false);
                    dispatch(addPseudoMessage(response));
                })
                .finally(() => {
                    setScriptRetrying(false);
                });
        }
    };


    const handleToggle = () => {
        setViewScript(!viewScript);
    };

    const handleRetryChoice = (rerun: boolean) => {
        setOfferRetry(false);

        if (rerun) {
            setScriptRetrying(true);
            retryScripGeneration();
        }
    };

    const handleContextDone = (proceed: boolean) => {
        setPromptContext(false);
        if (proceed) {
            onSend();
        }
        
    };

    // Handle reasoning-only message (during initial processing before response)
    if (!message.request && !message.response && message.reasoning) {
        return (
            <ReasoningAccordion
                messages={message.reasoning.message}
                processing={isProcessing}
                defaultOpen={true}
                processNotificationIndex={processNotificationIndex}
            />
        );
    }

    if (message.request && !message.response) {
        return (
            <>
                <TextMessage
                    message={message.request.userInput ?? ''}
                    isBot={false}
                    hardcoded={hardcoded}
                />
                {message.reasoning ? (
                    <ReasoningAccordion
                        messages={message.reasoning.message}
                        processing={isProcessing}
                        defaultOpen={true}
                        processNotificationIndex={processNotificationIndex}
                    />
                ) : isProcessing && (
                    <ReasoningAccordion
                        messages={['Processing your request...']}
                        processing={true}
                        defaultOpen={true}
                        processNotificationIndex={0}
                    />
                )}
            </>
        );
    }
    
    else if (message.request && message.response) {
        // Check if the exception is "cancelled" and show cancellation notification
        if (message.response.exception === "cancelled") {
            return (
                <>
                    <TextMessage
                        message={message.request.userInput ?? ''}
                        isBot={false}
                        hardcoded={hardcoded}
                    />
                    {message.reasoning && (
                        <ReasoningAccordion
                            messages={message.reasoning.message}
                            processing={false}
                            defaultOpen={true}
                            processNotificationIndex={processNotificationIndex}
                        />
                    )}
                    <CancellationTextMessage message='Chat execution was cancelled.' />
                    <Card className={classes.feedbackContainer}>
                          <FeedbackBar
                            confidenceText={"Unapplicable"}
                            errorMessage={message.script?.script_error}
                            sessionId={settings.sessionId}
                        />
                    </Card>
                </>
            );
        }
        
        if ((message.response.status === 500 || message.response.exception || message.response?.gptResponseParsingException) && message.response.exception !== "cancelled") {
            return (<ScriptRetryPrompt rerunChoice={handleRetryChoice} customMsg={true}/>);
         }
        const isText = !message.response.script || !message.response.segment;

        // Generate the markdown list from titleContextNeeded
        const titleContextMarkdown = message.response.missingTitleContext
            ?.length
            ? `##### Titles Needing Context:\n${message.response.missingTitleContext
                  .map(
                      (item) =>
                          `- [${item}](#titleaction-${encodeURIComponent(
                              item
                          )})`
                  )
                  .join('\n')}`
            : '';

        // Generate the markdown list from products
        const productsMarkdown = message.response.missingProductContext?.length
            ? `##### Products Needing Context:\n${message.response.missingProductContext
                  .map(
                      (item) =>
                          `- [${item}](#productaction-${encodeURIComponent(
                              item
                          )})`
                  )
                  .join('\n')}`
            : '';

        // Combine responseText with headers and markdown lists
        const combinedMessage = [
            message.response.responseText ?? '',
            titleContextMarkdown,
            productsMarkdown,
        ]
            .filter(Boolean)
            .join('\n\n'); // filter(Boolean) removes empty strings

        return (
            <>
                <TextMessage
                    message={
                        message.request.userInput ??
                        message.request.courseCorrection ??
                        ''
                    }
                    isBot={false}
                    hardcoded={hardcoded}
                />
                {message.reasoning && (
                    <ReasoningAccordion
                        messages={message.reasoning.message}
                        processing={isProcessing}
                        defaultOpen={true}
                        processNotificationIndex={processNotificationIndex}
                    />
                )}
                <Card className={classes.messageContainer}>
                    {hardcoded ? 
                    <AccessInstructions></AccessInstructions>
                     :
                    <TextMessage
                        message={
                            combinedMessage ??
                            message.response.unparsedGptResponse
                        }
                        isBot={true}
                        hardcoded={hardcoded}
                    />
                    }
                    
                    {!isText && (
                        <div>
                            <LuaScriptMessage
                                chatUser={message.request}
                                chatInteraction={message.response}
                                preScriptResult={message.script}
                                showZeroCountBanner={setZeroCountBanner}
                                messageIndex={messageIndex}
                                messageSequenceIndex={messageSequenceIndex}
                            />
                            <div>
                                <Button
                                    appearance="subtle"
                                    iconPosition="after"
                                    className={classes.toggleButton}
                                    onClick={handleToggle}
                                    icon={
                                        viewScript ? (
                                            <ChevronUp20Regular />
                                        ) : (
                                            <ChevronDown20Regular />
                                        )
                                    }
                                >
                                    <Subtitle2>Script & Logic Used</Subtitle2>
                                </Button>
                                <Collapse in={viewScript}>
                                    <div
                                        className={
                                            classes.segmentScriptContainer
                                        }
                                    >
                                        <Segment
                                            segmentText={
                                                message.response.segment ?? ''
                                            }
                                        />
                                        <Script
                                            script={
                                                message.response.script ?? ''
                                            }
                                        />
                                    </div>
                                </Collapse>
                            </div>
                        </div>
                    )}
                </Card> 
                {!isText && lastMessage && showZeroCountBanner && <ZeroCountBanner></ZeroCountBanner>}
                {!isPopupVisible && !isText && lastMessage && (
                    <Card className={classes.feedbackContainer}>
                          <FeedbackBar
                            confidenceText={message.response.confidence}
                            errorMessage={message.script?.script_error}
                            sessionId={settings.sessionId}
                        />
                    </Card>
                )}
                {message.script &&
                    message.script.script_error &&
                    offerRetry &&
                    lastMessage && (
                        
                        <ScriptRetryPrompt rerunChoice={handleRetryChoice} customMsg={false} />
                    )}
                {isScriptRetrying ? <Spinner size="large"></Spinner> : null}
                {((message.response.missingProductContext &&
                    message.response.missingProductContext?.length > 0) ||
                    (message.response.missingTitleContext &&
                        message.response.missingTitleContext?.length > 0)) &&
                    promptContext &&
                    lastMessage && <ContextPrompt onDone={handleContextDone}/> }
            </>
                    
        );
    } else {
        return <></>;
    }
};

export default Message;
